import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Mail, Save, Eye, Send, AlarmClock, ChevronDown, ChevronUp,
  CheckCircle, AlertTriangle, Sparkles, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_GROUPS = [
  {
    label: 'Welcome emails (sent automatically on signup)',
    icon: Sparkles,
    keys: ['welcome_monthly', 'welcome_one_time', 'welcome_prayer', 'magic_link'],
  },
  {
    label: 'Scheduled monthly reminders (admin gets a heads-up on the 1st)',
    icon: Calendar,
    keys: [
      'reminder_september', 'reminder_november', 'reminder_january',
      'reminder_february', 'reminder_march', 'reminder_april', 'reminder_may',
    ],
  },
];

const TEMPLATE_LABELS = {
  welcome_monthly: 'Monthly Supporter — Welcome',
  welcome_one_time: 'One-Time Gift — Welcome',
  welcome_prayer: 'Prayer Supporter — Welcome',
  magic_link: 'Magic-Link Sign-In',
  reminder_september: 'September Reminder',
  reminder_november: 'November Reminder',
  reminder_january: 'January Reminder',
  reminder_february: 'February Reminder',
  reminder_march: 'March Reminder',
  reminder_april: 'April Reminder',
  reminder_may: 'May Reminder (we arrived!)',
};

export const EmailTemplatesTab = ({ onMessage }) => {
  const [templates, setTemplates] = useState({});
  const [meta, setMeta] = useState({ variables: [], reminder_months: [] });
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewLang, setPreviewLang] = useState('en');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingKey, setSendingKey] = useState(null);
  const [triggering, setTriggering] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-templates`, { withCredentials: true });
      const { _meta, ...tpls } = data;
      setTemplates(tpls);
      setMeta(_meta || { variables: [], reminder_months: [] });
      const initialDrafts = {};
      Object.keys(tpls).forEach(k => {
        initialDrafts[k] = JSON.parse(JSON.stringify(tpls[k]));
      });
      setDrafts(initialDrafts);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const isDirty = (key) => {
    const a = templates[key];
    const b = drafts[key];
    if (!a || !b) return false;
    return a.en.subject !== b.en.subject || a.en.body !== b.en.body
        || a.no.subject !== b.no.subject || a.no.body !== b.no.body;
  };

  const updateDraft = (key, lang, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [key]: { ...prev[key], [lang]: { ...prev[key][lang], [field]: value } }
    }));
  };

  const save = async (key) => {
    setSavingKey(key);
    try {
      const payload = drafts[key];
      const { data } = await axios.put(`${API}/admin/email-templates/${key}`, payload,
        { withCredentials: true });
      setTemplates(prev => ({ ...prev, [key]: data }));
      setDrafts(prev => ({ ...prev, [key]: JSON.parse(JSON.stringify(data)) }));
      onMessage?.(`Saved: ${TEMPLATE_LABELS[key]}`);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  const preview = async (key, lang) => {
    setPreviewKey(key);
    setPreviewLang(lang);
    setPreviewHtml('');
    setPreviewSubject('');
    try {
      const { subject, body } = drafts[key][lang];
      const { data } = await axios.post(`${API}/admin/email-templates/preview`,
        { lang, subject, body, name: 'Maria' },
        { withCredentials: true });
      setPreviewSubject(data.subject);
      setPreviewHtml(data.body);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Preview failed');
    }
  };

  const sendTest = async (key) => {
    if (!testEmail || !testEmail.includes('@')) {
      onMessage?.('Enter a test email address first');
      return;
    }
    setSendingKey(key);
    try {
      const { data } = await axios.post(`${API}/admin/email-templates/send`,
        { template_key: key, recipient_group: 'monthly_pledgers', test_email: testEmail },
        { withCredentials: true });
      onMessage?.(`Sent ${data.sent}/${data.total} test email${data.sent === 1 ? '' : 's'} to ${testEmail}`);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingKey(null);
    }
  };

  const sendBroadcast = async (key) => {
    const label = TEMPLATE_LABELS[key];
    if (!window.confirm(
      `Send "${label}" to ALL monthly pledgers right now?\n\nThis will be sent in each supporter's preferred language (EN/NO).`
    )) return;
    setSendingKey(key);
    try {
      const { data } = await axios.post(`${API}/admin/email-templates/send`,
        { template_key: key, recipient_group: 'monthly_pledgers' },
        { withCredentials: true });
      onMessage?.(`Broadcast: ${data.sent}/${data.total} sent${data.failed ? `, ${data.failed} failed` : ''}`);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSendingKey(null);
    }
  };

  const triggerReminderNow = async () => {
    if (!window.confirm(
      'Trigger the monthly admin reminder right now?\n\nThis sends YOU (admin) a heads-up email with the current month\'s draft so you can review and broadcast.'
    )) return;
    setTriggering(true);
    try {
      const { data } = await axios.post(`${API}/admin/email-templates/trigger-reminder`, {},
        { withCredentials: true });
      onMessage?.(`Reminder triggered for ${data.month}. Check your inbox.`);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Trigger failed');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return <div data-testid="email-templates-loading" className="p-6 text-slate-500">Loading templates…</div>;
  }

  return (
    <div data-testid="admin-email-templates-tab" className="space-y-6">
      {/* Intro */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-start gap-3">
          <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-slate-900">Email Templates</h2>
            <p className="text-sm text-slate-600 mt-1">
              Edit the wording, subject lines and CTAs of every automated and scheduled email — in
              both English and Norwegian. Welcome emails fire automatically when a supporter signs up
              (based on their pledge type and country). Reminder emails are queued — on the 1st of
              <strong> Sep, Nov, Jan, Feb, Mar, Apr, May </strong>
              you'll get an admin heads-up so you can tweak and broadcast.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {meta.variables.map(v => (
                <code key={v} className="bg-slate-100 text-slate-700 px-2 py-1 rounded-md">{'{' + v + '}'}</code>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Click any chip to copy — paste it into a subject or body to inject that value.
            </p>
          </div>
        </div>
      </div>

      {/* Test recipient + manual trigger */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1">
          <label className="block text-sm font-semibold text-slate-800 mb-1">Send a test email to:</label>
          <input
            data-testid="email-template-test-email"
            type="email"
            placeholder="you@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="w-full sm:w-96 px-4 py-2 rounded-xl border border-amber-300 bg-white focus:ring-2 focus:ring-amber-400 outline-none"
          />
          <p className="text-xs text-slate-600 mt-1">
            Used for the "Send test" button on each template card below.
          </p>
        </div>
        <button
          data-testid="trigger-reminder-now-btn"
          onClick={triggerReminderNow}
          disabled={triggering}
          className="inline-flex items-center gap-2 px-5 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 disabled:opacity-50"
        >
          <AlarmClock className="w-4 h-4" />
          {triggering ? 'Triggering…' : 'Trigger reminder now (test)'}
        </button>
      </div>

      {/* Template groups */}
      {TEMPLATE_GROUPS.map(group => {
        const GroupIcon = group.icon;
        return (
          <div key={group.label} className="space-y-3">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <GroupIcon className="w-4 h-4 text-blue-600" />
              <span>{group.label}</span>
            </div>
            {group.keys.map(key => {
              const draft = drafts[key];
              if (!draft) return null;
              const dirty = isDirty(key);
              const isOpen = openKey === key;
              return (
                <div
                  key={key}
                  data-testid={`email-template-${key}`}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <button
                    type="button"
                    data-testid={`email-template-${key}-toggle`}
                    onClick={() => setOpenKey(isOpen ? null : key)}
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <span className="font-semibold text-slate-900">{TEMPLATE_LABELS[key]}</span>
                      {dirty && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Unsaved
                        </span>
                      )}
                      {!dirty && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full inline-flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Saved
                        </span>
                      )}
                    </div>
                    {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-6 space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {['en', 'no'].map(lang => (
                          <div key={lang} className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl leading-none">{lang === 'en' ? '🇬🇧' : '🇳🇴'}</span>
                              <span className="font-semibold text-slate-800">
                                {lang === 'en' ? 'English' : 'Norsk'}
                              </span>
                              <button
                                type="button"
                                onClick={() => preview(key, lang)}
                                data-testid={`email-template-${key}-${lang}-preview`}
                                className="ml-auto text-xs inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                              >
                                <Eye className="w-3 h-3" /> Preview
                              </button>
                            </div>
                            <label className="block text-xs font-medium text-slate-500 mt-2">Subject</label>
                            <input
                              data-testid={`email-template-${key}-${lang}-subject`}
                              type="text"
                              value={draft[lang].subject}
                              onChange={(e) => updateDraft(key, lang, 'subject', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                            />
                            <label className="block text-xs font-medium text-slate-500 mt-2">Body (HTML)</label>
                            <textarea
                              data-testid={`email-template-${key}-${lang}-body`}
                              value={draft[lang].body}
                              onChange={(e) => updateDraft(key, lang, 'body', e.target.value)}
                              rows={12}
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-mono"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
                        <button
                          data-testid={`email-template-${key}-save`}
                          onClick={() => save(key)}
                          disabled={!dirty || savingKey === key}
                          className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-40"
                        >
                          <Save className="w-4 h-4" />
                          {savingKey === key ? 'Saving…' : 'Save changes'}
                        </button>
                        <button
                          data-testid={`email-template-${key}-send-test`}
                          onClick={() => sendTest(key)}
                          disabled={sendingKey === key || !testEmail}
                          className="inline-flex items-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 disabled:opacity-40"
                        >
                          <Send className="w-4 h-4" />
                          {sendingKey === key ? 'Sending…' : 'Send test to me'}
                        </button>
                        {key.startsWith('reminder_') && (
                          <button
                            data-testid={`email-template-${key}-broadcast`}
                            onClick={() => sendBroadcast(key)}
                            disabled={sendingKey === key}
                            className="inline-flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 disabled:opacity-40"
                          >
                            <Send className="w-4 h-4" />
                            {sendingKey === key ? 'Sending…' : 'Broadcast to all monthly pledgers now'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Preview modal */}
      {previewKey && (
        <div
          data-testid="email-template-preview-modal"
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewKey(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">{TEMPLATE_LABELS[previewKey]} — {previewLang === 'en' ? 'English' : 'Norsk'}</p>
                <p className="font-semibold text-slate-900 mt-1">{previewSubject}</p>
              </div>
              <button
                data-testid="email-template-preview-close"
                onClick={() => setPreviewKey(null)}
                className="text-slate-400 hover:text-slate-700 text-xl px-2"
              >
                ×
              </button>
            </div>
            <div className="overflow-auto bg-slate-50 p-4 flex-1">
              <div
                className="max-w-xl mx-auto bg-white rounded-xl border border-slate-200 p-6 text-slate-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
